import { Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { createPortal } from 'react-dom';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const VIEW_CONFIG = 'config';
const VIEW_PROVIDERS = 'providers';

const ChatInputModelSelector = ({
  providers = [],
  value: controlledValue,
  provider: controlledProvider,
  defaultProvider,
  defaultValue,
  onChange,
  onProviderChange,
  disabled = false,
  alignMenu = 'right',
  placeholder = 'Select model',
  configTitle = 'AI Configuration',
  providersTitle = 'Select AI Provider',
  changeButtonLabel = 'Change',
}) => {
  const wrapperRef = useRef(null);
  const dropdownRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState(VIEW_CONFIG);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const [internalProvider, setInternalProvider] = useState(null);
  const [internalValue, setInternalValue] = useState(null);

  const providerOptions = useMemo(
    () => providers.map((p) => ({ value: p.provider, label: p.label ?? p.provider })),
    [providers]
  );

  const firstProviderValue = providerOptions[0]?.value ?? null;
  const firstModelValue = providers[0]?.models?.[0]?.value ?? null;

  const provider =
    controlledProvider !== undefined ? controlledProvider : internalProvider ?? defaultProvider ?? firstProviderValue;
  const value = controlledValue !== undefined ? controlledValue : internalValue ?? defaultValue ?? firstModelValue;

  const currentModels = useMemo(
    () => (provider != null ? providers.find((p) => p.provider === provider)?.models ?? [] : []),
    [provider, providers]
  );
  const selectedModel = currentModels.find((m) => m.value === value);
  const selectedProvider = providerOptions.find((p) => p.value === provider);
  const triggerLabel = selectedModel?.label ?? placeholder;

  const updateDropdownPosition = useCallback(() => {
    const root = wrapperRef.current;
    if (!root) return;
    const triggerRect = root.getBoundingClientRect();
    const menuRect = dropdownRef.current?.getBoundingClientRect();
    const menuWidth = menuRect?.width ?? 280;
    const menuHeight = menuRect?.height ?? 0;

    const top = Math.max(8, triggerRect.top - 8);
    const rawLeft = alignMenu === 'left'
      ? triggerRect.right - menuWidth
      : triggerRect.left;
    const left = Math.max(8, Math.min(rawLeft, window.innerWidth - menuWidth - 8));

    setDropdownPos({
      top: Math.max(top, menuHeight + 8),
      left,
    });
  }, [alignMenu]);

  const openDropdown = useCallback(() => {
    if (disabled) return;
    setView(VIEW_CONFIG);
    setIsOpen(true);
  }, [disabled]);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleProviderChange = (providerValue) => {
    if (controlledProvider === undefined) setInternalProvider(providerValue);
    const foundProvider = providers.find((p) => p.provider === providerValue);
    const nextModel = foundProvider?.models?.[0]?.value ?? null;
    if (controlledValue === undefined) setInternalValue(nextModel);
    onProviderChange?.(providerValue, foundProvider);
    if (nextModel != null) {
      onChange?.(nextModel, foundProvider?.models?.find((m) => m.value === nextModel));
    }
    setView(VIEW_CONFIG);
  };

  const handleModelChange = (nextValue) => {
    if (controlledValue === undefined) setInternalValue(nextValue);
    onChange?.(nextValue, currentModels.find((m) => m.value === nextValue));
    closeDropdown();
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') closeDropdown();
    };
    const handleClickOutside = (event) => {
      if (wrapperRef.current?.contains(event.target)) return;
      if (dropdownRef.current?.contains(event.target)) return;
      closeDropdown();
    };
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [closeDropdown, isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    updateDropdownPosition();
    const raf = requestAnimationFrame(updateDropdownPosition);
    const onLayoutChange = () => updateDropdownPosition();
    window.addEventListener('resize', onLayoutChange);
    window.addEventListener('scroll', onLayoutChange, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onLayoutChange);
      window.removeEventListener('scroll', onLayoutChange, true);
    };
  }, [isOpen, updateDropdownPosition, view]);

  const menu = isOpen
    ? createPortal(
        <div
          ref={dropdownRef}
          className="fixed z-[1000] min-w-[240px] max-w-[320px] overflow-hidden rounded-lg border border-edge bg-surface shadow-[0px_4px_12px_rgba(0,0,0,0.08)] dark:shadow-[0px_4px_12px_rgba(0,0,0,0.45)]"
          style={{ top: dropdownPos.top, left: dropdownPos.left, transform: 'translateY(-100%)' }}
        >
          {view === VIEW_CONFIG ? (
            <>
              <div className="px-4 py-2 text-sm font-semibold text-content-secondary">{configTitle}</div>
              <div className="border-t border-edge px-4 py-2">
                <div className="mb-2 flex items-center justify-between gap-2 text-xs text-content-muted">
                  <span>{selectedProvider?.label ?? 'Provider'}</span>
                  <button
                    type="button"
                    onClick={() => setView(VIEW_PROVIDERS)}
                    className="inline-flex items-center gap-1 rounded px-1 text-content hover:bg-surface-hover"
                  >
                    {changeButtonLabel}
                    <ChevronRight size={12} />
                  </button>
                </div>
                <div className="scrollbar-aimsos-auto-sm max-h-[220px] overflow-y-auto">
                  {currentModels.map((model) => (
                    <button
                      key={model.value}
                      type="button"
                      onClick={() => handleModelChange(model.value)}
                      className="flex w-full items-center justify-between gap-2 rounded px-2 py-2 text-left text-sm text-content hover:bg-surface-hover-subtle"
                    >
                      <span>{model.label}</span>
                      {value === model.value ? <Check size={14} className="text-brand-600 dark:text-brand-400" /> : null}
                    </button>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 border-b border-edge px-4 py-2">
                <button
                  type="button"
                  onClick={() => setView(VIEW_CONFIG)}
                  className="rounded p-1 hover:bg-surface-hover"
                >
                  <ChevronLeft size={14} />
                </button>
                <span className="text-sm font-semibold text-content-secondary">{providersTitle}</span>
              </div>
              <div className="scrollbar-aimsos-auto-sm max-h-[220px] overflow-y-auto p-2">
                {providerOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => handleProviderChange(opt.value)}
                    className="flex w-full items-center justify-between rounded px-2 py-2 text-left text-sm text-content hover:bg-surface-hover-subtle"
                  >
                    <span>{opt.label}</span>
                    {provider === opt.value ? <Check size={14} className="text-brand-600 dark:text-brand-400" /> : null}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>,
        document.body
      )
    : null;

  return (
    <>
      <div ref={wrapperRef} className="relative inline-block">
        <button
          type="button"
          disabled={disabled}
          onClick={isOpen ? closeDropdown : openDropdown}
          className="inline-flex h-6 items-center gap-1 rounded-full bg-surface px-3 text-sm font-normal text-content-secondary hover:bg-surface-hover disabled:opacity-50"
        >
          <span className="max-w-[180px] truncate">{triggerLabel}</span>
          <ChevronRight size={14} className={isOpen ? 'rotate-90' : ''} />
        </button>
      </div>
      {menu}
    </>
  );
};

export default ChatInputModelSelector;
